import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShul, useUpdateShul } from '../../../hooks/useShuls';
import { ScheduleEntry } from '../../../types';
import AdminGuidance from '../AdminGuidance';
import ScheduleList from '../ScheduleList';
import ScheduleUpload from '../ScheduleUpload';
import ShulImagesEditor from '../ShulImagesEditor';
import { ScheduleEntryFormData } from '../ScheduleEntryForm';
import { questionById } from '../../../config/setupQuestions';
import { getAnswer } from '../../../utils/setupAnswers';
import YearSetupWizard from '../setup/YearSetupWizard';
import YearChecklist from '../setup/YearChecklist';
import OneDateEditor from '../setup/OneDateEditor';

type TimesView = 'year' | 'oneDate' | 'advanced';

/**
 * The shul-only tabs (Times, Posters, Guidance), moved here unchanged from the
 * old admin page. They keep using the old Shul shape through useShul().
 */
export default function ShulMinyanTabs({ shulId, tab }: { shulId: string; tab: 'times' | 'posters' | 'guidance' }) {
  const { t } = useTranslation();
  const { shul, loading, error } = useShul(shulId);
  const { updateShul, error: updateError } = useUpdateShul(shulId);
  const [view, setView] = useState<TimesView>('year');
  const [wizardOpen, setWizardOpen] = useState(false);

  if (tab === 'guidance') return <AdminGuidance />;
  if (loading) return <p className="text-sm text-ash">{t('common.loading')}</p>;
  if (error) return <p className="text-sm text-danger-600">{error}</p>;
  if (!shul) return <p className="text-sm text-ink">{t('shul.notFound')}</p>;

  const persistSchedules = async (schedules: ScheduleEntry[]) => {
    await updateShul({ schedules });
  };

  const handleAddSchedule = async (data: ScheduleEntryFormData) => {
    const entry: ScheduleEntry = {
      id: crypto.randomUUID(),
      ...data,
      overrides: [],
    };
    await persistSchedules([...(shul.schedules || []), entry]);
  };

  const handleUpdateSchedule = async (entryId: string, data: ScheduleEntryFormData) => {
    // Keep every field the form sends (night, Selichos start…) and the entry's date changes.
    const next = (shul.schedules || []).map((s) =>
      s.id === entryId ? { ...data, id: s.id, overrides: s.overrides || [] } : s
    );
    await persistSchedules(next);
  };

  const handleDeleteSchedule = async (entryId: string) => {
    await persistSchedules((shul.schedules || []).filter((s) => s.id !== entryId));
  };

  if (tab === 'posters') return <ShulImagesEditor shul={shul} />;

  const schedules = shul.schedules || [];
  const weekdayShacharit = questionById('wd.shacharit');
  const neverSetUp = !!weekdayShacharit && getAnswer(schedules, weekdayShacharit).mode === 'unset';
  const views: TimesView[] = ['year', 'oneDate', 'advanced'];
  const viewLabel: Record<TimesView, string> = {
    year: t('setup.ui.checklistTitle'),
    oneDate: t('setup.oneDate.title'),
    advanced: t('setup.ui.advanced'),
  };

  return (
    <div className="space-y-4">
      {neverSetUp ? (
        <div className="rounded-[16px] border border-weekday-600/30 bg-weekday-50 p-4 space-y-2">
          <h3 className="text-base font-semibold text-ink">{t('setup.ui.setupTitle')}</h3>
          <p className="text-sm text-ink">{t('setup.ui.setupIntro')}</p>
          <button
            type="button"
            onClick={() => setWizardOpen(true)}
            className="px-4 py-2 rounded-[10px] bg-ink text-paper text-sm font-medium"
          >
            {t('setup.ui.startSetup')}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setWizardOpen(true)}
          className="px-4 py-2 rounded-[10px] border border-ink/20 text-sm font-medium text-ink"
        >
          {t('setup.ui.runAgain')}
        </button>
      )}

      <div className="flex gap-2 overflow-x-auto pb-1" role="tablist">
        {views.map((v) => (
          <button
            key={v}
            type="button"
            role="tab"
            aria-selected={view === v}
            onClick={() => setView(v)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-medium border ${
              view === v ? 'bg-ink text-paper border-ink' : 'border-ink/20 text-ink'
            }`}
          >
            {viewLabel[v]}
          </button>
        ))}
      </div>

      {updateError && <p className="text-sm text-danger-600">{updateError}</p>}

      {view === 'year' && <YearChecklist schedules={schedules} onSave={persistSchedules} />}
      {view === 'oneDate' && <OneDateEditor shul={shul} onSave={persistSchedules} />}
      {view === 'advanced' && (
        <div className="space-y-4">
          <p className="text-sm text-ash">{t('setup.ui.advancedIntro')}</p>
          <ScheduleUpload schedules={schedules} onUpload={persistSchedules} />
          <ScheduleList
            schedules={schedules}
            onAdd={handleAddSchedule}
            onUpdate={handleUpdateSchedule}
            onDelete={handleDeleteSchedule}
            onReorder={persistSchedules}
          />
        </div>
      )}

      {wizardOpen && (
        <YearSetupWizard schedules={schedules} onSave={persistSchedules} onClose={() => setWizardOpen(false)} />
      )}
    </div>
  );
}
